import { CheckCircle2, Clock, AlertTriangle, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";

function formatDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function AccessCallout() {
  const { mode } = useAuth();
  const { subscription, loading, isActive, isTrialing, isPastDue, isCanceled } = useSubscription();

  if (mode !== "real" || loading) return null;

  const periodEnd = formatDate(subscription?.current_period_end ?? null);

  let tone: "ok" | "warn" | "danger" | "muted" = "ok";
  let Icon = CheckCircle2;
  let title = "";
  let body = "";
  let cta: { label: string; to: string } | null = null;

  if (!subscription) {
    tone = "muted";
    Icon = Lock;
    title = "No active subscription";
    body = "You're on the free preview. Start a 14-day trial to unlock the full dashboard — you won't be charged until day 15, and you can cancel anytime.";
    cta = { label: "See plans", to: "/pricing" };
  } else if (isTrialing) {
    tone = "ok";
    Icon = Clock;
    title = "Trial active — full dashboard access";
    body = periodEnd
      ? `Your trial ends on ${periodEnd}. On that date your card will be charged automatically and your subscription continues. Cancel anytime before then in Manage subscription to avoid the charge — you'll keep access until ${periodEnd}.`
      : "Your trial is active. You'll be charged automatically when it ends; cancel anytime before then to avoid the charge.";
  } else if (isPastDue) {
    tone = "danger";
    Icon = AlertTriangle;
    title = "Payment failed — access at risk";
    body = "Your last payment didn't go through. Paddle is retrying — update your card in Manage subscription before the retries end or you'll lose dashboard access.";
  } else if (isCanceled) {
    tone = "warn";
    Icon = Clock;
    title = "Subscription canceled";
    body = periodEnd
      ? `You still have full dashboard access until ${periodEnd}. After that, the dashboard locks and your data is preserved — resubscribe anytime to restore access.`
      : "Access continues until the end of your billing period.";
    cta = { label: "Resubscribe", to: "/pricing" };
  } else if (isActive) {
    tone = "ok";
    Icon = CheckCircle2;
    title = "Active — full dashboard access";
    body = periodEnd
      ? `Your subscription renews automatically on ${periodEnd}. No action needed.`
      : "Your subscription is active. No action needed.";
  } else {
    tone = "muted";
    Icon = Lock;
    title = "Dashboard locked";
    body = "Start a subscription to regain full access.";
    cta = { label: "See plans", to: "/pricing" };
  }

  const toneClasses = {
    ok: "bg-primary/5 border-primary/20 text-foreground",
    warn: "bg-amber-500/5 border-amber-500/30 text-foreground",
    danger: "bg-destructive/10 border-destructive/30 text-foreground",
    muted: "bg-muted/30 border-border text-foreground",
  }[tone];

  const iconClasses = {
    ok: "text-primary",
    warn: "text-amber-500",
    danger: "text-destructive",
    muted: "text-muted-foreground",
  }[tone];

  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${toneClasses}`}>
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${iconClasses}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</p>
        {cta && (
          <Link to={cta.to} className="inline-block mt-2">
            <Button size="sm" variant="outline">{cta.label}</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
